import React from 'react';
import {DefaultTheme} from 'styled-components';
import {OpeningHour} from 'domain/usecases/private/service-points/get-service-points';
import {translations} from 'presentation/translations-new';
import {ServicePointStatus} from './types';

const {open, closed, closesAt, opensAt, openedToday, closedToday, opensSoon, closesSoon} =
	translations['pt-br'].privateFlow.help.ourAddresses;
const {at} = translations['pt-br'].commons;

const WEEK_DAYS = [
	'Domingo',
	'Segunda-feira',
	'Terça-feira',
	'Quarta-feira',
	'Quinta-feira',
	'Sexta-feira',
	'Sábado',
];

const ONE_HOUR_IN_MINUTES = 60;

const parseTimeToMinutes = (time: string): number | null => {
	const match = /^(\d{2}):(\d{2})$/.exec(time);
	if (!match) return null;

	const hours = Number(match[1]);
	const minutes = Number(match[2]);
	if (hours > 23 || minutes > 59) return null;

	return hours * 60 + minutes;
};

const getCurrentServicePointHour = (
	hours: OpeningHour[],
	now: Date = new Date(),
): OpeningHour | undefined => {
	const currentWeekDay = WEEK_DAYS[now.getDay()];
	return hours.find(hour => hour.dia === currentWeekDay);
};

const getServicePointStatus = (
	hour: OpeningHour | undefined,
	now: Date = new Date(),
): ServicePointStatus => {
	if (!hour) return 'closed';

	const openingMinutes = parseTimeToMinutes(hour.abertura);
	const closingMinutes = parseTimeToMinutes(hour.fechamento);
	if (openingMinutes === null || closingMinutes === null) return 'closed';

	const currentMinutes = now.getHours() * 60 + now.getMinutes();

	if (currentMinutes >= openingMinutes && currentMinutes < closingMinutes) {
		return closingMinutes - currentMinutes <= ONE_HOUR_IN_MINUTES ? 'closing-soon' : 'open';
	}

	const minutesUntilOpening = openingMinutes - currentMinutes;
	if (minutesUntilOpening > 0 && minutesUntilOpening <= ONE_HOUR_IN_MINUTES) {
		return 'opening-soon';
	}

	return 'closed';
};

const getStatusLabel = (status: ServicePointStatus): string => {
	switch (status) {
		case 'open':
			return open;
		case 'closing-soon':
			return closesSoon;
		case 'opening-soon':
			return opensSoon;
		default:
			return closed;
	}
};

const getStatusColor = (
	status: ServicePointStatus,
	theme: DefaultTheme,
): React.CSSProperties['color'] => {
	switch (status) {
		case 'open':
			return theme.palette.success.main;
		case 'opening-soon':
		case 'closing-soon':
			return theme.palette.warning.main;
		default:
			return theme.palette.error.main;
	}
};

const getStatusDetail = (
	status: ServicePointStatus,
	currentHour: OpeningHour | undefined,
	now: Date = new Date(),
): string | null => {
	if (status === 'open' || status === 'closing-soon') {
		return currentHour ? `${closesAt} ${currentHour.fechamento}` : null;
	}

	if (status === 'opening-soon') {
		return currentHour ? `${opensAt} ${currentHour.abertura}` : null;
	}

	if (!currentHour) return closedToday;

	const openingMinutes = parseTimeToMinutes(currentHour.abertura);
	const currentMinutes = now.getHours() * 60 + now.getMinutes();

	if (openingMinutes !== null && currentMinutes < openingMinutes) {
		return `${opensAt} ${currentHour.abertura}`;
	}

	return `${openedToday} ${currentHour.abertura} ${at} ${currentHour.fechamento}`;
};

export {
	getCurrentServicePointHour,
	getServicePointStatus,
	getStatusColor,
	getStatusDetail,
	getStatusLabel,
	parseTimeToMinutes,
};
export type {ServicePointStatus};
